/**
 * Catálogo cross-playbook de quick replies (Brief 4 · Gestor).
 *
 * Tabela consolidada (rótulo · conteúdo · atalho · playbooks que usam · ações)
 * sobre a agregação client-side de `GET /v1/playbooks` (D1). Busca client-side.
 * Edição reusa o editor de quick replies da Tela 1 (modal sobre o playbook dono)
 * e só aparece com `playbook-author` (D2). Colunas sem fonte de backend
 * (categoria · uso 30d · atendentes · ghost) ficam fora — honest-defer (D3).
 */

import { Pencil, Reply } from 'lucide-react';
import { useMemo, useState } from 'react';

import { aggregateQuickReplies, filterQuickReplies } from './aggregate';

import type { QuickReplyOwner } from './aggregate';
import type { PlaybookDto } from '../playbooks/types';

function OwnerChips({
  owners,
  canAuthor,
  onEdit,
}: {
  owners: QuickReplyOwner[];
  canAuthor: boolean;
  onEdit: (playbookId: string) => void;
}): JSX.Element {
  return (
    <div className="qr-owner-chips">
      {owners.map((owner) =>
        canAuthor ? (
          <button
            key={owner.playbookId}
            type="button"
            className="qr-owner-chip qr-owner-chip--action"
            title={`Editar quick replies de “${owner.playbookName}”`}
            onClick={() => {
              onEdit(owner.playbookId);
            }}
          >
            {owner.playbookName}
          </button>
        ) : (
          <span key={owner.playbookId} className="qr-owner-chip">
            {owner.playbookName}
          </span>
        ),
      )}
    </div>
  );
}

export function QuickRepliesCatalogView({
  playbooks,
  canAuthor,
  onEdit,
}: {
  playbooks: PlaybookDto[];
  canAuthor: boolean;
  onEdit: (playbookId: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const all = useMemo(() => aggregateQuickReplies(playbooks), [playbooks]);
  const items = useMemo(() => filterQuickReplies(all, query), [all, query]);

  return (
    <div className="quick-replies-screen">
      <div className="qr-pagehead">
        <div className="qr-pagehead__heading">
          <div className="qr-pagehead__crumbs">Configuração da operação</div>
          <div className="qr-pagehead__title">Quick replies</div>
          <div className="qr-pagehead__sub">
            Visão consolidada das respostas rápidas de todos os playbooks.
          </div>
        </div>
      </div>

      <div className="qr-panel">
        <div className="qr-panel__head">
          <h2 className="qr-panel__title">Catálogo cross-playbook</h2>
          <span className="qr-count">{all.length}</span>
          <span className="qr-panel__spacer" />
          <input
            type="search"
            className="qr-search"
            placeholder="Buscar rótulo, conteúdo ou playbook…"
            aria-label="Buscar quick replies"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
          />
        </div>

        {all.length === 0 ? (
          <p className="qr-empty">
            <Reply size={16} /> Nenhuma quick reply definida nos playbooks deste tenant.
          </p>
        ) : items.length === 0 ? (
          <p className="qr-empty">Nenhuma quick reply corresponde à busca “{query}”.</p>
        ) : (
          <table className="qr-table">
            <thead>
              <tr>
                <th>Rótulo · Conteúdo</th>
                <th>Atalho</th>
                <th>Playbooks que usam</th>
                {canAuthor && <th className="qr-table__actions-col">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((qr) => (
                <tr key={qr.key}>
                  <td>
                    <div className="qr-cell-label">“{qr.label}”</div>
                    <div className="qr-cell-text">{qr.text}</div>
                  </td>
                  <td>
                    {qr.shortcut !== undefined && qr.shortcut !== '' ? (
                      <kbd className="qr-kbd">/{qr.shortcut}</kbd>
                    ) : (
                      <span className="qr-muted">—</span>
                    )}
                  </td>
                  <td>
                    <OwnerChips owners={qr.owners} canAuthor={canAuthor} onEdit={onEdit} />
                  </td>
                  {canAuthor && (
                    <td className="qr-table__actions-col">
                      <button
                        type="button"
                        className="qr-btn qr-btn--sm"
                        onClick={() => {
                          onEdit(qr.owners[0]!.playbookId);
                        }}
                      >
                        <Pencil size={13} /> Editar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
